/**
 * Sublayer titles containing any of these substrings are excluded from search.
 * They're either too generic, world-scale, or carry no useful searchable text.
 */
export const SKIP_LAYER_PATTERNS = [
  'crossing',
  'roadcenterline',
  'boundary',
  'ssap',
  'marker',
  'railroad',
  'zone',
  'weather',
  'event',
  'ordered',
  'size',
  'severity',
];

/**
 * Sublayer titles containing any of these substrings are treated as
 * world-scale traffic layers and bounded to the current viewport rather than
 * the jurisdiction polygon.
 */
export const TRAFFIC_LAYER_PATTERNS = ['traffic', 'road closure'];

/**
 * Field-name patterns used to filter which string fields are searched on
 * generic GIS layers. A field is included if its name (case-insensitive)
 * contains any of these substrings.
 */
export const GIS_SEARCH_FIELD_PATTERNS = [
  'name',
  'phone',
  'telephone',
  'cell',
  'owner',
  'own',
  'address',
  'addr',
  'location',
  'home',
  'parcel',
  'street',
  'apn',
  'pin',
  'pid',
  'lot',
  'badge',
  'unit',
  'status',
  'label',
  'title',
  'num',
  'desc',
  'type',
  'incident',
  'incidenttype',
  'facility',
  'operator',
  'provider',
  'agency',
  'organization',
  'org',
  'business',
  'company',
  'manager',
  'tenant',
  'contact',
  'site',
  'place',
];

/**
 * Field patterns used when the query is primarily a person / business /
 * facility name. Broader than `GIS_SEARCH_FIELD_PATTERNS` for name-like
 * fields (so e.g. a health or shelter layer with a `FACILITY`, `OPERATOR`,
 * or `PROVIDER` field is still searched), and narrower on address/number
 * fields that would only add noise for a name query.
 */
export const GIS_NAME_FIELD_PATTERNS = [
  'name',
  'owner',
  'own',
  'label',
  'title',
  'desc',
  'facility',
  'operator',
  'provider',
  'agency',
  'organization',
  'org',
  'business',
  'company',
  'manager',
  'tenant',
  'contact',
  'site',
  'place',
  'full',
  'first',
  'last',
  'middle',
  'person',
  'resident',
  'occupant',
  'licensee',
];

export const GIS_PHONE_FIELD_PATTERNS = [
  'phone',
  'telephone',
  'tele',
  'cell',
  'mobile',
  'fax',
  'contact',
  'call',
  'num',
  'sms',
  'dial',
];

export const GIS_PARCEL_FIELD_PATTERNS = [
  'parcel',
  'parno',
  'apn',
  'pin',
  'pid',
  'lot',
  'num',
  'id',
  'name',
  'owner',
  'own',
];

/** Maximum place rows from the geocoder pass, local and global together. */
export const MAX_POI = 5;

/**
 * Display priority for grouped search categories. Buckets earlier in this
 * list render first in the dropdown, regardless of which source streamed
 * results in first. Categories not listed here fall through to "everything
 * else" and keep their original first-seen order below the listed groups.
 *
 * This array is the filter-badge order. Present categories render in this
 * sequence, skipping any bucket with no hits:
 *   1. Calls
 *   2. Responders
 *   3. Dynamic Layers
 *   4. what3words
 *   5. RapidSOS Locations
 *   6. Local Places & Addresses
 *   7. Global Places & Addresses
 *   8. Places & Addresses
 *   9. Parcels
 *   10. Transcripts
 *   11. Messages
 *   12. Additional Data (phone lookup)
 *   13. Additional Data (MedicAlert lookup)
 *   14. Everything else (alerts, static layers, hydrants, other GIS, ...)
 *
 * Full predictive-search behavior (debounce, source breadth, explicit-submit
 * differences) is documented in `GlobalMapSearch.product-guide.md`.
 */
export const CATEGORY_PRIORITY_ORDER: string[] = [
  'call',
  'axon',
  'dynamic',
  'what3words',
  'place-rsos',
  'place-local',
  'place-global',
  'place',
  'parcel',
  'transcript',
  'message',
  'rade-adr',
  'rade-maf',
];

/** Display labels for the fixed (non-GIS) result categories. */
export const FIXED_CATEGORY_LABELS: Record<string, string> = {
  place: 'Places & Addresses',
  'place-rsos': 'RapidSOS Locations',
  'place-local': 'Local Places & Addresses',
  'place-global': 'Global Places & Addresses',
  what3words: 'what3words',
  hydrant: 'Hydrants',
  parcel: 'Parcels',
  axon: 'Responders',
  static: 'Static Layers',
  dynamic: 'Dynamic Layers',
  call: 'Calls',
  transcript: 'Transcripts',
  message: 'Messages',
  alert: 'Alerts',
  'rade-maf': 'Additional Data',
  'rade-adr': 'Additional Data',
};
